import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Lottie } from 'lottie-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useQaza } from '../../contexts/QazaContext';
import { useTranslation } from 'react-i18next';
import { crescentPulseLottie, starSparkleLottie } from '../../data/lottieAnimations';

// ── Custom Islamic-themed navigation SVGs ───────────────────────────────────

const HomeIcon = ({ active, color, size = 27 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Crescent moon behind arch */}
    <path d="M14 3C10 3 7.5 5.5 7.5 8.5A6.5 6.5 0 0014 15a6.5 6.5 0 006.5-6.5C20.5 5.5 18 3 14 3z"
      fill={color} opacity="0.15" />
    {/* Mosque arch + dome */}
    <path d="M14 2C10.5 2 8 4.5 8 8c0 2.2 1.1 4.2 2.8 5.4L10 25h8l-.8-11.6C18.9 12.2 20 10.2 20 8c0-3.5-2.5-6-6-6z"
      fill={color} opacity="0.2" />
    <path d="M5 25h18M9 25V14a5 5 0 0110 0v11" stroke={color} strokeWidth={active ? 1.8 : 1.5} strokeLinecap="round" />
    <path d="M14 8C12 8 10.5 9 10.5 10.5S12 13 14 13s3.5-1 3.5-2.5S16 8 14 8z"
      fill={color} opacity={active ? 0.9 : 0.5} />
    {/* Minaret left */}
    <line x1="6" y1="13" x2="6" y2="25" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    <path d="M5 13 Q6 11 7 13" stroke={color} strokeWidth="1.2" fill="none" strokeLinecap="round" />
    {/* Minaret right */}
    <line x1="22" y1="13" x2="22" y2="25" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    <path d="M21 13 Q22 11 23 13" stroke={color} strokeWidth="1.2" fill="none" strokeLinecap="round" />
  </svg>
);

const QiblaIcon = ({ active, color, size = 27 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="14" cy="14" r="11" stroke={color} strokeWidth={active ? 1.8 : 1.4} />
    <circle cx="14" cy="14" r="7" stroke={color} strokeWidth="0.8" opacity="0.4" />
    {/* Islamic 8-point star center */}
    <path d="M14 9l.8 2.2L17 12l-2.2.8L14 15l-.8-2.2L11 12l2.2-.8z" fill={color} opacity={active ? 0.9 : 0.5} />
    {/* Compass needle pointing NE (Makkah direction) */}
    <path d="M14 14L18 10" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M14 14L10 18" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
    {/* N S E W markers */}
    <circle cx="14" cy="3.5" r="1" fill={color} opacity="0.8" />
    <circle cx="14" cy="24.5" r="0.8" fill={color} opacity="0.4" />
    <circle cx="3.5" cy="14" r="0.8" fill={color} opacity="0.4" />
    <circle cx="24.5" cy="14" r="0.8" fill={color} opacity="0.4" />
  </svg>
);

const TrackerIcon = ({ active, color, size = 27 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Crescent base */}
    <path d="M14 4C9.5 4 6 7.5 6 12c0 4.5 3.5 8 8 8 1.5 0 2.9-.4 4.1-1.1A8.5 8.5 0 0014 4z"
      fill={color} opacity="0.12" />
    {/* Prayer rug / Sajdah mat */}
    <rect x="6" y="16" width="16" height="8" rx="1.5" stroke={color} strokeWidth={active ? 1.8 : 1.4} />
    {/* Mihrab arch on rug */}
    <path d="M11 24v-4a3 3 0 016 0v4" stroke={color} strokeWidth="1.3" strokeLinecap="round" fill="none" />
    {/* Star on the rug */}
    <path d="M14 9l.6 1.6L16 11l-1.6.6L14 13l-.6-1.6L12 11l1.6-.6z"
      fill={color} opacity={active ? 0.9 : 0.5} />
    {/* Streak flame */}
    <path d="M20 8c0 2.2-1.5 3.5-3 4 .5-1 .8-2 .5-3 .5.5.8 1 1 2C19.5 10 20 9 20 8z"
      fill={color} opacity={active ? 0.8 : 0.4} />
  </svg>
);

const SettingsIcon = ({ active, color, size = 27 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* 8-point Islamic geometric star as settings icon */}
    <path d="M14 5l1.5 3.5L19 7l-1.5 3.5L21 14l-3.5 1.5L19 19l-3.5-1.5L14 21l-1.5-3.5L9 19l1.5-3.5L7 14l3.5-1.5L9 9l3.5 1.5z"
      stroke={color} strokeWidth={active ? 1.7 : 1.4} fill={active ? color : 'none'}
      fillOpacity={active ? 0.12 : 0} strokeLinejoin="round" />
    <circle cx="14" cy="14" r="3" stroke={color} strokeWidth={active ? 1.8 : 1.4}
      fill={active ? color : 'none'} fillOpacity={active ? 0.3 : 0} />
  </svg>
);

// ── BottomNav ────────────────────────────────────────────────────────────────

const BottomNav = () => {
  const location = useLocation();
  const { isDarkMode, isRTL } = useTheme();
  const { pending } = useQaza();
  const { t } = useTranslation();

  const activeColor  = isDarkMode ? '#f59e0b' : 'var(--p-hex)';
  const inactiveColor = isDarkMode ? '#cbd5e1' : '#4b5563';

  const navItems = [
    {
      path: '/',
      label: t('nav.home'),
      render: (active) => <HomeIcon active={active} color={active ? activeColor : inactiveColor} />,
    },
    {
      path: '/qibla',
      label: t('nav.qibla'),
      render: (active) => <QiblaIcon active={active} color={active ? activeColor : inactiveColor} />,
    },
    // ── CENTER: Quran ──
    {
      path: '/quran',
      label: t('nav.quran'),
      matchPrefix: true,
      isCenter: true,
      render: (active) => active
        ? (
          <div className="relative w-[28px] h-[28px]" style={{ animation: 'cta-breathe 2.4s ease-in-out infinite' }}>
            <svg width="28" height="28" viewBox="0 0 32 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 3.5 Q3 1 5 1.5 Q9 2.5 14 3.5 L14 25.5 Q9 24 5 25 Q3 25.5 2 24 Z" fill={activeColor} opacity="0.22" stroke={activeColor} strokeWidth="1.4" strokeLinejoin="round"/>
              <path d="M18 3.5 Q23 2.5 27 1.5 Q29 1 30 3.5 L30 24 Q29 25.5 27 25 Q23 24 18 25.5 Z" fill={activeColor} opacity="0.22" stroke={activeColor} strokeWidth="1.4" strokeLinejoin="round"/>
              <line x1="16" y1="1.5" x2="16" y2="26.5" stroke={activeColor} strokeWidth="2" strokeLinecap="round"/>
              <path d="M14 1.5 Q16 0 18 1.5" stroke={activeColor} strokeWidth="1.3" fill="none" strokeLinecap="round"/>
              {[0,1,2,3].map(i => <line key={i} x1={5} y1={8+i*3.5} x2={13} y2={7.5+i*3.5} stroke={activeColor} strokeWidth="1.1" strokeLinecap="round" opacity="0.85"/>)}
              {[0,1,2,3].map(i => <line key={i} x1={19} y1={8+i*3.5} x2={27} y2={7.5+i*3.5} stroke={activeColor} strokeWidth="1.1" strokeLinecap="round" opacity="0.85"/>)}
            </svg>
          </div>
        )
        : (
          <svg width="27" height="27" viewBox="0 0 32 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 3.5 Q3 1 5 1.5 Q9 2.5 14 3.5 L14 25.5 Q9 24 5 25 Q3 25.5 2 24 Z" fill={inactiveColor} opacity="0.12" stroke={inactiveColor} strokeWidth="1.1" strokeLinejoin="round"/>
            <path d="M18 3.5 Q23 2.5 27 1.5 Q29 1 30 3.5 L30 24 Q29 25.5 27 25 Q23 24 18 25.5 Z" fill={inactiveColor} opacity="0.12" stroke={inactiveColor} strokeWidth="1.1" strokeLinejoin="round"/>
            <line x1="16" y1="1.5" x2="16" y2="26.5" stroke={inactiveColor} strokeWidth="1.8" strokeLinecap="round"/>
            <path d="M14 1.5 Q16 0 18 1.5" stroke={inactiveColor} strokeWidth="1.2" fill="none" strokeLinecap="round"/>
            {[0,1,2,3].map(i => <line key={i} x1={5} y1={8+i*3.5} x2={13} y2={7.5+i*3.5} stroke={inactiveColor} strokeWidth="0.9" strokeLinecap="round" opacity="0.65"/>)}
            {[0,1,2,3].map(i => <line key={i} x1={19} y1={8+i*3.5} x2={27} y2={7.5+i*3.5} stroke={inactiveColor} strokeWidth="0.9" strokeLinecap="round" opacity="0.65"/>)}
          </svg>
        ),
    },
    {
      path: '/tracker',
      label: t('nav.tracker'),
      badge: pending.length,
      render: (active) => active
        ? <Lottie src={starSparkleLottie} loop={true} autoplay={true} style={{ width: 28, height: 28 }} />
        : <TrackerIcon active={active} color={inactiveColor} />,
    },
    {
      path: '/settings',
      label: t('nav.settings'),
      render: (active) => <SettingsIcon active={active} color={active ? activeColor : inactiveColor} />,
    },
  ];

  return (
    <nav
      className={`fixed bottom-7 left-0 right-0 z-50 ${
        isDarkMode
          ? 'bg-[var(--bg-dark-from)]/95 backdrop-blur-xl border-t border-white/8'
          : 'bg-white/95 backdrop-blur-xl border-t border-gray-200'
      }`}
      style={{ boxShadow: isDarkMode ? 'none' : '0 -4px 24px rgba(0,0,0,0.06)' }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="max-w-lg mx-auto px-1">
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => {
            const isActive = item.matchPrefix
              ? location.pathname.startsWith(item.path)
              : location.pathname === item.path;

            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace(/\//g, '') || 'home'}`}
                className={`relative flex flex-col items-center justify-center w-14 h-full ${
                  isActive
                    ? isDarkMode ? 'text-amber-400' : 'text-[var(--p-hex)]'
                    : isDarkMode ? 'text-gray-200' : 'text-gray-600'
                }`}
              >
                {/* Active pill */}
                {isActive && (
                  <span className="absolute top-2 w-6 h-1 rounded-full"
                    style={{ background: isDarkMode ? '#f59e0b' : 'var(--p-hex)' }} />
                )}

                {/* Icon — center Quran tab is slightly larger */}
                <div className={`relative mt-1.5 transition-transform duration-200 ${
                  isActive ? 'scale-110' : item.isCenter ? 'scale-105 opacity-90' : 'scale-100 opacity-80'
                }`}>
                  {item.render(isActive)}
                  {/* Badge */}
                  {item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] px-0.5 text-[9px] font-bold rounded-full bg-amber-500 text-white flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>

                {/* Label */}
                <span className={`text-[10px] leading-none mt-0.5 ${isActive ? 'font-bold' : 'font-medium'}`}>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;
